
import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { fetchAvailableNowListings } from '../../../containers/SearchPage/SearchPage.duck';
import css from './AvailableNowCarousel.module.css';
import Slider from 'react-slick';
import IconCollection from '../../IconCollection/IconCollection';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../../util/routes';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createSlug } from '../../../util/urlHelpers';
import { addToWishlist, removeFromWishlist, wishListListingsSelector } from '../../../containers/WishListPage/WishListPage.duck';
import { IconSpinner, AspectRatioWrapper, ResponsiveImage } from '../..';
import { isAuthenticatedSelector } from '../../../ducks/auth.duck';
import { useConfiguration } from '../../../context/configurationContext';

const settings = {
  dots: false,
  infinite: false,
  // speed: 2000,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 2000,
  // cssEase: 'ease-in-out',
  pauseOnHover: false,
  swipeToSlide: true,
  centerMode: false,

  responsive: [
    {
      breakpoint: 1200,
      settings: { slidesToShow: 3 },
    },
    {
      breakpoint: 768,
      settings: { slidesToShow: 2 },
    },
    {
      breakpoint: 480,
      settings: { slidesToShow: 1.1 },
    },
  ],
};

const iconHeart = <IconCollection iconName="outlineHeart" />
const iconHeartFilled = <IconCollection iconName="heartFilled" />


const AvailableNowCarousel = props => {
  const history = useHistory();
  const dispatch = useDispatch();
  const wishlistListings = useSelector(wishListListingsSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const [addToWishlistLoading, setIsAddToWishlistLoading] = useState(null);
  const routeConfiguration = useRouteConfiguration();
  const { rootClassName, className, listings, loading, error, fetchAvailableNowListings } = props;
  const classes = classNames(rootClassName || css.root, className);
  const intl = useIntl();
  const [locationDenied, setLocationDenied] = useState(false);
  const config = useConfiguration();
  const marketplaceCurrency = config.currency;


  useEffect(() => {
    const handleError = () => {
      setLocationDenied(true);
      fetchAvailableNowListings({ latitude: 0, longitude: 0 }, false, config);
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => fetchAvailableNowListings(pos.coords, true, config), handleError);
    } else {
      handleError();
    }
  }, [fetchAvailableNowListings, config]);

  if (loading) {
    return (
      <div className={css.message}>
        <div>
          <FormattedMessage id="AvailableNowCarousel.loading" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.message}>
        <div>Error: {error.message}</div>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className={css.message}>
        <div>
          <FormattedMessage id="availableNowCarousel.noListings" />
        </div>
      </div>
    );
  }

  const handleViewDetail = (listing) => {
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        {
          id: listing.id.uuid,
          slug: createSlug(listing.attributes.title),
        },
        {}
      )
    );
  }


  const addListingToWishlist = (listing) => {
    dispatch(addToWishlist(listing.id.uuid)).then(() => {
      setIsAddToWishlistLoading(null);
    });
  };

  const removeListingFromWishlist = (listing) => {
    dispatch(removeFromWishlist(listing.id.uuid)).then(() => {
      setIsAddToWishlistLoading(null);
    });
  };

  const handleWishlistClick = (listing, isWishlisted) => {
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }

    if (addToWishlistLoading) return;

    setIsAddToWishlistLoading(listing.id.uuid);
    if (isWishlisted) {
      removeListingFromWishlist(listing);
    } else {
      addListingToWishlist(listing);
    }
  };


  return (
    <div className={css.carouselContainer}>
      {
        locationDenied ? (
          <div className={css.message}>
            <div>
              <FormattedMessage id="availableNowCarousel.locationDenied" />
            </div>
          </div>
        ) : null
      }

      <div>
        <Slider {...settings} className={css.carousel}>
          {listings.map(listing => {
            const { attributes, images } = listing;
            const { publicData } = attributes;
            const location = publicData?.location?.address;
            const { title, price } = attributes;
            const formattedHourPrice = intl.formatNumber(price?.amount / 100, {
              style: 'currency',
              currency: price?.currency || marketplaceCurrency,
            });
            const price_per_day = publicData?.price_per_day;
            const formattedDayPrice = intl.formatNumber(price_per_day, {
              style: 'currency',
              currency: marketplaceCurrency,
            });

            const firstImage = images && images.length > 0 ? images[0] : null;
            const {
              aspectWidth = 1,
              aspectHeight = 1,
              variantPrefix = 'listing-card',
            } = config.layout.listingImage;

            const variants = firstImage
              ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
              : [];

            const fallbackVariants = firstImage && variants.length === 0
              ? Object.keys(firstImage?.attributes?.variants).filter(k =>
                k.includes('scaled-medium') || k.includes('scaled-small') || k.includes('default') || k.includes('landscape-crop')
              )
              : [];

            const finalVariants = variants.length > 0 ? variants : fallbackVariants;



            const isFeatured = publicData?.isFeatured;
            const totalRatingSum = publicData?.totalRatingSum;
            const totalRatings = publicData?.totalRatings || 0;
            const averageRating = totalRatings ? Number((totalRatingSum / totalRatings).toFixed(1)) : '0';

            const isWishlisted = wishlistListings.some(item => item?.id?.uuid === listing?.id?.uuid);
            const loading = addToWishlistLoading === listing?.id?.uuid;

            return (
              <div key={listing.id} className={css.caroulseCard}>
                <div className={css.imageWrapper}>
                  {isFeatured && <span className={css.featured}><FormattedMessage id="AvailableNowCarousel.featured" /></span>}

                  <button onClick={() => handleWishlistClick(listing, isWishlisted)} className={css.wishlist}>
                    {loading ? <IconSpinner /> : isWishlisted ? iconHeartFilled : iconHeart}
                  </button>

                  <AspectRatioWrapper
                    width={aspectWidth}
                    height={aspectHeight}
                  >
                    <ResponsiveImage
                      alt={title}
                      image={firstImage}
                      variants={finalVariants}
                      rootClassName={css.image}
                    />
                  </AspectRatioWrapper>
                </div>

                <div className={css.cardDetails}>
                  <div className={css.details}>
                    <p className={css.price}>
                      {price ? (
                        <>
                          {formattedHourPrice} <span><FormattedMessage id="AvailableNowCarousel.perHour" /></span>
                        </>
                      ) : price_per_day ? (
                        <>
                          {formattedDayPrice} <span><FormattedMessage id="AvailableNowCarousel.perDay" /></span>
                        </>
                      ) : null}
                    </p>
                    <h4 className={css.title}>{title}</h4>
                    <p className={css.location}>
                      <IconCollection name="location" />
                      <span>{location}</span>
                    </p>

                    <div className={css.line} />

                    <div className={css.footer}>
                      <div className={css.rating}>
                        <IconCollection name="solidPinkHeart" />

                        <p>
                          {averageRating}/5 <span>({totalRatings})</span>
                        </p>
                      </div>
                      <button onClick={() => handleViewDetail(listing)} className={css.viewBtn}>
                        <FormattedMessage id="listingViewDetailBtn" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </Slider>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const { listings, error, loading } = state.SearchPage.availableNow;
  return { listings, error, loading };
};

const mapDispatchToProps = { fetchAvailableNowListings };

export default connect(mapStateToProps, mapDispatchToProps)(AvailableNowCarousel);
